/**
 * A small in-page channel between the guided first visit and the parts of the world it follows: the lesson menu
 * asks whether the tour takes a Start Lesson / Start Quiz press, the tutor session says when the tutor has
 * finished an answer, and the badge moment says while it is playing.
 */
const listeners = {};

export function on(name, listener) {
  (listeners[name] = listeners[name] || new Set()).add(listener);
  return () => listeners[name].delete(listener);
}

export function emit(name, payload) {
  (listeners[name] || []).forEach(listener => {
    try {
      listener(payload);
    } catch (error) {
      console.warn(`[firstVisit] listener for ${name} failed`, error);
    }
  });
}

/**
 * The lesson menu calls this before starting a lesson or a quiz. A guided step that owns the press (the tour's
 * own short slide or quiz) handles it and returns true; otherwise the menu does what it always does.
 */
let menuInterceptor = null;
export function setMenuInterceptor(interceptor) {
  menuInterceptor = interceptor;
  return () => {
    if (menuInterceptor === interceptor) menuInterceptor = null;
  };
}
export function interceptMenuPress(kind, lessonId) {
  try {
    return Boolean(menuInterceptor && menuInterceptor(kind, String(lessonId || '')));
  } catch (error) {
    console.warn('[firstVisit] menu interceptor failed', error);
    return false;
  }
}

/** Exit goes Home (not to Quikbase) while the guided visit's last step asks for it. */
let leaveHome = false;
export const setLeaveHome = value => {
  leaveHome = !!value;
};
export const wantsLeaveHome = () => leaveHome;

/**
 * Whether the guided first visit is running for this learner, for the parts of the world that would otherwise open
 * on top of it: the first-Quikbase tutorial video and a course's intro video stay closed - and unmarked, so they
 * still show later - while it runs (Sol tour-collisions Q1 A). Callers wait for the answer, briefly.
 */
let guided = { decided: false, active: false };
const guidedWaiters = [];
export function setGuidedVisit(active) {
  guided = { decided: true, active: !!active };
  guidedWaiters.splice(0).forEach(resolve => resolve(guided.active));
}
/** The guided visit's state if already known, else null: the Quikbase landing cannot wait (Unity lands at once). */
export const guidedVisitKnown = () => (guided.decided ? guided.active : null);

/**
 * Where the learner landed in Quikbase on this load (Sol tour-first-entry C): {landed: 'portal' | 'central', target}
 * with target = the launched course's portal {grade, subject}, or null while no course is known.
 */
let landing = null;
export function setQuikbaseLanding(value) {
  landing = value || null;
  emit('quikbase-landing', landing);
}
export const quikbaseLanding = () => landing;

export function guidedVisitActive({ timeoutMs = 4000 } = {}) {
  if (guided.decided) return Promise.resolve(guided.active);
  return new Promise(resolve => {
    guidedWaiters.push(resolve);
    setTimeout(() => resolve(guided.decided ? guided.active : false), timeoutMs);
  });
}

/**
 * A course portal's unit menu waits while the guided visit is on a Quikbase step before "Walk into the glowing
 * portal" (Sol tour-portal-spawn A): the learner lands in front of their course's portal, and the tour's first
 * "Press W" would otherwise open the menu over every later step. The world reports each portal entry and exit
 * here; when the hold ends with the learner still inside a portal, its menu opens at once.
 */
let portalHeld = false;
let insidePortal = null;
let openPortalMenu = null;
export function setPortalMenuOpener(opener) {
  openPortalMenu = opener;
  return () => {
    if (openPortalMenu === opener) openPortalMenu = null;
  };
}
/** The avatar entered a course portal ({grade, subject}); true when its menu may open now. */
/**
 * The tour is on its tutor step, so a tutor started now is its one free question: the token request says so and the
 * server decides (Sol round4 B5, tour-question-unmetered A). The lesson's own tutor, later, is metered as usual.
 */
/**
 * While the tour's own steps run, only the current step's control works; every other world button looks the same but
 * says "Available after the tour" (owner round 4, Sol round4; Sol onboarding-leftovers L2 A - enforced here, in one
 * place). `allowed` is the set of test ids that still work, or null when nothing is gated.
 */
let tourGate = null;
export function setTourGate(allowed) {
  const wasGated = Boolean(tourGate);
  tourGate = allowed ? new Set(allowed) : null;
  if (wasGated !== Boolean(tourGate)) emit('tour-gate', Boolean(tourGate));
}
export const tourGateActive = () => Boolean(tourGate);
export const tourBlocks = testId => Boolean(tourGate && testId && !tourGate.has(testId));
/** Wraps a world button's handler: a gated press does nothing but say why. */
export function guardTourPress(testId, press) {
  return (...args) => {
    if (tourBlocks(testId)) {
      emit('tour:blocked', testId);
      return undefined;
    }
    return press ? press(...args) : undefined;
  };
}

/** The tour is leading the learner in the course world: the course box stays folded meanwhile (Sol onboarding-dev-acc-1 E2 A). */
let worldGuide = false;
export function setWorldGuide(active) {
  const next = !!active;
  if (next === worldGuide) return;
  worldGuide = next;
  emit('world-guide', next);
}
export const worldGuideActive = () => worldGuide;

/**
 * "Type instead" / "Talk instead" ends one tutor session and starts the other: one stop and one start that are not the
 * learner finishing or opening the tutor. The tour skips exactly those two (measured on DEV 2026-10-10: the switch on
 * the lesson's closing question moved the guide on to Upgrade).
 */
let switchEdges = 0;
let switchAt = 0;
export function noteTutorSwitch() {
  switchEdges = 2;
  switchAt = Date.now();
}
export function consumeTutorSwitchEdge() {
  if (switchEdges > 0 && Date.now() - switchAt < 10000) {
    switchEdges -= 1;
    return true;
  }
  switchEdges = 0;
  return false;
}

let tourTutorStep = false;
export const setTourTutorStep = active => {
  tourTutorStep = !!active;
};
export const tutorPurpose = () => (tourTutorStep ? 'first-visit' : undefined);

/** Opens a course portal's unit menu as walking into it would (the tour's "Take me to my course"). */
export function openCourseMenu(portal) {
  if (!portal || !openPortalMenu) return false;
  try {
    openPortalMenu(portal);
    return true;
  } catch (error) {
    console.warn('[firstVisit] could not open the course menu', error?.message);
    return false;
  }
}

export function portalEntered(portal) {
  insidePortal = portal || null;
  return !portalHeld;
}
export function portalExited() {
  insidePortal = null;
}
export function setPortalHeld(held) {
  const released = portalHeld && !held;
  portalHeld = !!held;
  if (released && insidePortal && openPortalMenu) {
    try {
      openPortalMenu(insidePortal);
    } catch (error) {
      console.warn('[firstVisit] opening the held portal failed', error);
    }
  }
}
export const portalIsHeld = () => portalHeld;

// The tour's free tutor question offers its course's own questions on the tutor's bar (owner prototype; set by the tour).
let suggestionsForTour = null;
export const setTourSuggestions = list => {
  suggestionsForTour = Array.isArray(list) && list.length ? list : null;
};
export const tourSuggestions = () => suggestionsForTour;

// The tutor's microphone step (message / help) is on screen: the tour's own guide steps back meanwhile.
let micGateOpen = false;
export const setMicGateOpen = open => {
  if (micGateOpen === Boolean(open)) return;
  micGateOpen = Boolean(open);
  emit('mic-gate', micGateOpen);
};
export const micGateIsOpen = () => micGateOpen;

// While the tour leads in the course world, the HUD hides what the owner's prototype hides there - the plan pill, Exit
// and the FLY / LASER / JUMP / HELP tiles (Sol proto-port P2 A); everything else stays, greyed by the tour gate.
let tourHide = false;
export const setTourHide = value => {
  if (tourHide === Boolean(value)) return;
  tourHide = Boolean(value);
  emit('tour-hide', tourHide);
};
export const tourHideActive = () => tourHide;
export const TOUR_HIDDEN_TILES = ['fly', 'laser', 'jump', 'help'];
